import type { InferOutput } from 'valibot'
import type { HtmlSanitizationPolicy } from '~/utils/html-sanitization'
import type { solrHighlightingSchema } from '~/utils/solr/highlighting'

type HighlightFields = InferOutput<typeof solrHighlightingSchema>[string]

/**
 * Checks if a document has highlights in content fields (not just title/author).
 * Used to determine whether to forward search query to destination page.
 * When search matches only title or author (keywords_lc), we don't forward the query.
 */
export function hasContentHighlights(docHighlight: HighlightFields | undefined): boolean {
  if (!docHighlight) {
    return false
  }
  return Boolean(docHighlight.bodytext?.length || docHighlight.abstract?.length)
}

export function createHighlightRegex(term: string): RegExp {
  const pattern = `\\b${term}\\b`
  return new RegExp(pattern, 'gi')
}

export function cleanHighlights(highlights: string[]): string[] {
  return highlights.flatMap(h =>
    (h.match(/<span class="highlight">(.*?)<\/span>/g) || []).map(match =>
      match.replace(/<span class="highlight">|<\/span>/g, ''),
    ),
  )
}

export function applyHighlightsToWords(html: string, highlights: string[], highlightId = ''): string {
  let content = html
  let firstMatch = false

  highlights.forEach((term) => {
    const decodedTerm = term.toLowerCase()
    const escapedTerm = decodedTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    // Match term with flexible boundaries, ignoring HTML tags
    const regex = createHighlightRegex(escapedTerm)

    content = content.replace(regex, (match) => {
      const lowerMatch = match.toLowerCase()
      if (/<span class="highlight">/.test(match)) {
        return match
      }
      if (lowerMatch.includes(decodedTerm) && !firstMatch && highlightId) {
        firstMatch = true
        return `<span id="${highlightId}"><span class="highlight">${match}</span></span>`
      }
      if (lowerMatch.includes(decodedTerm)) {
        return `<span class="highlight">${match}</span>`
      }
      return match
    })
  })

  return content
}

export function removeExistingHighlights(content: string): string {
  return content.replace(/<span class="highlight">(.*?)<\/span>/g, '$1')
}

export function handleHighlights(bodytext: string, highlights: string[], searchResultId: string, policy: HtmlSanitizationPolicy = 'richText') {
  const { $sanitizeHtml } = useNuxtApp()
  const sanitizedContent = $sanitizeHtml(bodytext || '', policy)
  const contentWithoutExistingHighlights = removeExistingHighlights(sanitizedContent)
  const highlightedContent = applyHighlightsToWords(contentWithoutExistingHighlights, highlights, searchResultId)
  return highlightedContent
}
